import { useMemo, useState } from "react";
import classes from "./InputDishCost.module.css";
import SearchBarAccordion from "../../components/panel/SearchBarAccordion";
import fetchWithAuth from "../../libs/fetchWithAuth";
import { useSelector } from "react-redux";
import { deptLongToShort } from "../../utils/ultis";
import Loader from "../../components/UI/Loader";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

const localUri = process.env.REACT_APP_API_URI + "/dishes";

export default function InputDishCost() {
  const [editingIds, setEditingIds] = useState(new Set());
  const [tempCosts, setTempCosts] = useState({});
  const [tempChecks, setTempChecks] = useState({});

  const [errors, setErrors] = useState({});
  const [init, setInit] = useState(true);
  const [foundData, setFoundData] = useState([]);
  const userProfile = useSelector((state) => state.auth.userCredential);
  const [isLoading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [keyword, setKeyword] = useState("");
  const [filterGroup, setFilterGroup] = useState("Món ăn");
  const [isUpdating, setIsUpdating] = useState(false);

  const dishMap = useMemo(() => {
    if (!Array.isArray(foundData)) return new Map();

    return new Map(foundData.map((item) => [item.id, item.data]));
  }, [foundData]);

  const userDept =
    deptLongToShort(userProfile.department) === "gm"
      ? "mng"
      : deptLongToShort(userProfile.department);

  const fetchAllData = async () => {
    try {
      const response = await fetchWithAuth(localUri + "/get-all", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "GET",
      });

      if (!response.ok) {
        throw new Error(response.message);
      }
      const data = await response.json();
      return data.dishData;
    } catch (err) {
      console.warn(err.message);
      setFetchError(err.message);
      return [];
    }
  };

  const updateCall = async (fbId, cost, isChecked) => {
    try {
      const response = await fetchWithAuth(localUri + "/update-dish-cost", {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({
          fbId,
          cost,
          isChecked,
        }),
      });
      const data = await response.json();

      if (!response.ok) {
        return {
          ok: false,
          message: `${data.message} - ${data.detail}`,
        };
      }
      return {
        ok: true,
        message: data.message,
        reportObj: data.reportObj,
      };
    } catch (err) {
      return {
        ok: false,
        message: `${err.message} - ${err.detail ? err.detail : ""}`,
      };
    }
  };

  const handleEdit = (id, cost) => {
    setEditingIds((prev) => new Set(prev).add(id));
    setTempCosts((prev) => ({ ...prev, [id]: cost }));
    setTempChecks((prev) => {
      if (prev[id]) return prev;
      const dbCostCheck = dishMap.get(id)?.costCheck || [];
      const dbChecks = dbCostCheck.flatMap((item) => {
        return item.isChecked ? deptLongToShort(item.department) : [];
      });
      return { ...prev, [id]: new Set(dbChecks) };
    });
  };

  const handleCancel = (id) => {
    setEditingIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setTempCosts((prev) => {
      const newCosts = { ...prev };
      delete newCosts[id];
      return newCosts;
    });
    setTempChecks((prev) => {
      const newChecks = { ...prev };
      delete newChecks[id];
      return newChecks;
    });
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[id];
      return newErrors;
    });
  };

  const handleCostChange = (id, value) => {
    const num = parseInt(value);
    if (isNaN(num) || num < 0) {
      setErrors((prev) => ({ ...prev, [id]: "Invalid number " }));
    } else {
      setErrors((prev) => ({ ...prev, [id]: "" }));
    }
    setTempCosts((prev) => ({ ...prev, [id]: parseInt(value, 10) }));
  };

  const handleCheckToggle = (id, key) => {
    // only toggle the value of tempcheck[id]
    // no logic to check wheter the tempChecks[id] is exist
    setTempChecks((prev) => {
      key = key === "mng" ? "gm" : key;
      const current = new Set(prev[id]);
      current.has(key) ? current.delete(key) : current.add(key);
      return { ...prev, [id]: current };
    });
  };

  const handleSave = async (id) => {
    setErrors((prev) => ({ ...prev, [id]: "" }));
    if (errors[id]) return;

    const cost = tempCosts[id];
    const submitUserDept = userDept === "mng" ? "gm" : userDept;
    const isChecked = (tempChecks[id] || new Set()).has(submitUserDept);

    const response = await updateCall(id, cost, isChecked);

    if (response.ok) {
      setIsUpdating(true);
      const updatedData = await fetchAllData();

      setFoundData(updatedData);
      setIsUpdating(false);

      handleCancel(id);
    } else {
      setErrors((prev) => ({ ...prev, [id]: response.message }));
    }
  };

  const fnWithSearchData = async (objFilter) => {
    setInit(false);
    setLoading(true);
    try {
      const data = await fetchAllData();
      setFoundData(data);
      const { keyword, filterGroup } = objFilter;
      const loweredKeyword = keyword.toLowerCase();
      setKeyword(loweredKeyword);
      setFilterGroup(filterGroup);
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAll = async () => {
    setLoading(true);

    try {
      const data = await fetchAllData();
      setFoundData(data);
      setInit(false);
    } catch (err) {
      console.warn(err.message);
      setFetchError(err.message);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const filterData = useMemo(() => {
    if (!Array.isArray(foundData) || foundData.length === 0) {
      return []; // Return an empty array if foundData is not valid
    }

    const filterMap = {
      "Món ăn": (dish) => dish.data.name.toLowerCase().includes(keyword), // Implicit return
      "Nhóm món ăn": (dish) => dish.data.group.toLowerCase() === keyword, // Implicit return
      Outlet: (dish) => dish.data.outlet.toLowerCase() === keyword, // Implicit return
      default: (dish) => true, // Implicit return
    };

    // const filterFn = filterMap[filterGroup];
    // return filterFn > 0 ? foundData.filter(filterFn) : foundData;
    const filterFn = filterMap[filterGroup] || filterMap.default;

    return foundData.filter(filterFn);
  }, [foundData, filterGroup, keyword]);

  const searchResultTable = (
    <div className="table-container">
      <table className={classes["editable-table"]}>
        <thead>
          <tr>
            <th>STT</th>
            <th>Dish</th>
            <th>Cost</th>
            <th>Check</th>
            <th>Control</th>
          </tr>
        </thead>
        <tbody>
          {Array.isArray(filterData) &&
            filterData?.map(({ id, data }, index) => {
              let shownId;

              const dbChecks = data?.costCheck || [];

              const cost = data?.cost?.value ? data.cost.value : 0;
              if (typeof id == "string") {
                shownId = id.slice(-5);
              } else {
                shownId = "###" + id.toString();
              }

              const costDisable = (id) => {
                if (!editingIds.has(id)) return true;
                const costCheckArr = dishMap.get(id)?.costCheck || [];
                return costCheckArr.some((item) => item.isChecked);
              };

              return (
                <tr key={id}>
                  <td>{index + 1}</td>
                  <td>
                    <div>
                      <span className={classes.label}>Món: </span>
                      <span className={classes["dish-name"]}>{data.name}</span>
                    </div>
                    <div>
                      <p>
                        <span className={classes.label}>Group: </span>
                        {data.group}
                      </p>
                    </div>
                    <div>
                      <p>
                        <span className={classes.label}>Outlet: </span>
                        {data.outlet}
                      </p>
                    </div>
                    <div>
                      <p>
                        <span className={classes.label}>ID5: </span>
                        {shownId}
                      </p>
                    </div>
                  </td>
                  <td>
                    {editingIds.has(id) ? (
                      <div className={classes["cost-input"]}>
                        <input
                          type="number"
                          key={`cost-${id}`}
                          autoFocus
                          value={
                            tempCosts[id] !== undefined
                              ? tempCosts[id]
                              : isNaN(cost)
                              ? ""
                              : cost
                          }
                          disabled={costDisable(id)}
                          onChange={(e) => handleCostChange(id, e.target.value)}
                        />
                        {errors[id] && (
                          <span className="error-text">{errors[id]}</span>
                        )}
                        {!errors[id] && (
                          <small className="previous-cost">
                            Previous: {cost.toLocaleString()}
                          </small>
                        )}
                      </div>
                    ) : (
                      <span className={classes.cost}>
                        {data?.cost?.value
                          ? data.cost.value.toLocaleString()
                          : 0}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className={classes["checkbox-group"]}>
                      {["acc", "mng"].map((dept) => {
                        const modDept = dept === "mng" ? "gm" : dept;
                        const deptCheck = dbChecks?.some((item) => {
                          return (
                            item.isChecked &&
                            deptLongToShort(item.department) === modDept
                          );
                        });

                        const tempCheckValue = editingIds.has(id)
                          ? tempChecks[id]?.has(modDept) || false
                          : deptCheck;

                        const checkDisable = (id) => {
                          if (!editingIds.has(id)) {
                            return true;
                          } else {
                            if (userDept === "mng") {
                              return false;
                            } else {
                              return dept !== userDept;
                            }
                          }
                        };

                        return (
                          <label
                            key={dept}
                            // className={
                            //   userDept === dept && editingIds.has(id)
                            //     ? "checkbox-label active"
                            //     : "checkbox-label inactive"
                            // }
                            className={
                              userDept === dept && editingIds.has(id)
                                ? `${classes["checkbox"]} ${classes.active}`
                                : `${classes["checkbox"]} ${classes.inactive}`
                            }
                          >
                            <input
                              type="checkbox"
                              checked={tempCheckValue}
                              disabled={checkDisable(id)}
                              onChange={() =>
                                editingIds.has(id) &&
                                handleCheckToggle(id, dept)
                              }
                            />
                            {dept.toUpperCase()}
                          </label>
                        );
                      })}
                    </div>
                  </td>
                  <td>
                    {editingIds.has(id) ? (
                      isUpdating ? (
                        <Loader />
                      ) : (
                        <>
                          <button
                            onClick={() => handleSave(id)}
                            disabled={!!errors[data.id]}
                            className={`${classes.icon} ${classes.check}`}
                          >
                            <CheckIcon />
                          </button>
                          <button
                            onClick={() => handleCancel(id)}
                            className={`${classes.icon} ${classes.cancel}`}
                          >
                            <CloseIcon />
                          </button>
                        </>
                      )
                    ) : (
                      <button
                        onClick={() =>
                          handleEdit(
                            id,
                            data?.cost?.value ? data.cost.value : ""
                          )
                        }
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );

  const noResult = !init && foundData?.length === 0;

  const validResult = !init && foundData?.length > 0;

  console.log("temp checks", tempChecks);

  return (
    <div className={classes.container}>
      <SearchBarAccordion onSearch={fnWithSearchData} loadAll={loadAll} />
      <div className={classes["search-notification"]}>
        {isLoading && <Loader />}
        {fetchError && <p className={classes["error-message"]}>{fetchError}</p>}
      </div>
      <div>
        <h5>Kết quả tìm kiếm - {` ${filterData.length} món ăn`}</h5>
        {init && <p>Vui lòng lựa chọn loại món ăn </p>}
        {noResult && <p>Tìm thấy 0 món ăn dựa theo bộ lọc của bạn.</p>}
        {validResult && searchResultTable}
      </div>
    </div>
  );
}
